import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useMap, Marker } from "react-leaflet";
import * as L from "leaflet";
import Supercluster from "supercluster";
import { DARKGREEN } from "@helper/colors";
import {
  parentTreeIcon,
  noSensorTreeIcon,
  unknownNoDataIcon,
  unknownMaintenanceIcon,
} from "@helper/marker";

export const createClusterIcon = (count) => {
  const size = count >= 100 ? 44 : count >= 25 ? 40 : 36;
  const fontSize = count >= 100 ? 13 : 12;

  return L.divIcon({
    className: "treesense-cluster-icon",
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      border-radius: 999px;
      background: ${DARKGREEN};
      border: 2px solid rgba(255, 255, 255, 0.92);
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: ${fontSize}px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
    ">${count}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const getTreeIcon = (tree) => {
  const health = tree.health_state;
  if (health === 3) {
    if (!tree.has_active_sensor) return noSensorTreeIcon();
    const sensorState = tree.active_sensor_state;
    if (sensorState === 1 || sensorState === 2) return unknownMaintenanceIcon();
    return unknownNoDataIcon();
  }
  return tree.has_active_sensor ? parentTreeIcon(health) : noSensorTreeIcon();
};

/**
 * Renders trees as clustered markers using Supercluster.
 *
 * @param {Object} props
 * @param {Array} props.trees - Trees with a `latlng` property (L.LatLng or {lat, lng}).
 * @param {Function} [props.onTreeClick] - Optional callback when a tree marker is clicked: (tree) => void.
 * @param {number|string} [props.selectedTreeId] - Optional ID of the currently selected tree (for highlight).
 * @param {Function} [props.resizeIcon] - Optional function to resize icon for selected tree: (icon, w, h) => icon.
 */
export default function TreeClusterLayer({ trees, onTreeClick, selectedTreeId, resizeIcon }) {
  const map = useMap();
  const [clusterFeatures, setClusterFeatures] = useState([]);
  const clusterIconCacheRef = useRef(new Map());

  const treeById = useMemo(() => new Map(trees.map((tree) => [Number(tree.id), tree])), [trees]);

  const points = useMemo(
    () =>
      trees.map((tree) => ({
        type: "Feature",
        properties: {
          cluster: false,
          treeId: Number(tree.id),
        },
        geometry: {
          type: "Point",
          coordinates: [tree.latlng.lng, tree.latlng.lat],
        },
      })),
    [trees]
  );

  const clusterIndex = useMemo(
    () =>
      new Supercluster({
        radius: 48,
        maxZoom: 17,
      }).load(points),
    [points]
  );

  const getClusterIcon = useCallback((count) => {
    const cacheKey = Number(count) || 0;
    if (!clusterIconCacheRef.current.has(cacheKey)) {
      clusterIconCacheRef.current.set(cacheKey, createClusterIcon(cacheKey));
    }
    return clusterIconCacheRef.current.get(cacheKey);
  }, []);

  const updateClusterFeatures = useCallback(() => {
    const bounds = map.getBounds();
    const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
    setClusterFeatures(clusterIndex.getClusters(bbox, Math.round(map.getZoom())));
  }, [clusterIndex, map]);

  useEffect(() => {
    updateClusterFeatures();
    map.on("moveend", updateClusterFeatures);
    map.on("zoomend", updateClusterFeatures);

    return () => {
      map.off("moveend", updateClusterFeatures);
      map.off("zoomend", updateClusterFeatures);
    };
  }, [map, updateClusterFeatures]);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => updateClusterFeatures());
    const t1 = setTimeout(() => updateClusterFeatures(), 120);
    const t2 = setTimeout(() => updateClusterFeatures(), 320);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [trees.length, updateClusterFeatures]);

  useEffect(() => {
    setClusterFeatures([]);
  }, [trees]);

  return clusterFeatures.map((feature) => {
    const properties = feature.properties || {};
    const [lng, lat] = feature.geometry.coordinates;
    const position = [lat, lng];

    if (properties.cluster) {
      const clusterId = properties.cluster_id;
      const count = properties.point_count || 0;
      if (clusterId == null) return null;

      return (
        <Marker
          key={`cluster-${clusterId}`}
          position={position}
          icon={getClusterIcon(count)}
          eventHandlers={{
            click: () => {
              const expansionZoom = Math.min(
                clusterIndex.getClusterExpansionZoom(clusterId),
                map.getMaxZoom()
              );
              map.flyTo(position, expansionZoom);
            },
          }}
        />
      );
    }

    const tree = treeById.get(Number(properties.treeId));
    if (!tree || !tree.latlng) return null;

    const baseIcon = getTreeIcon(tree);
    const isSelected = selectedTreeId != null && Number(tree.id) === Number(selectedTreeId);
    const markerIcon = isSelected && resizeIcon ? resizeIcon(baseIcon, 80, 80) : baseIcon;

    return (
      <Marker
        key={tree.id}
        position={tree.latlng}
        icon={markerIcon}
        eventHandlers={
          onTreeClick
            ? {
                click: () => onTreeClick(tree),
              }
            : undefined
        }
      />
    );
  });
}
